import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import FunctionsOutlinedIcon from '@mui/icons-material/FunctionsOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  GridColDef,
  GridValidRowModel,
} from '@mui/x-data-grid';

import {FDataGrid} from '../grid';

import {
  IndicatorCandidate,
  KpiPlanRow,
} from './activityplanning.models';

import IndicatorValueEditor, {
  IndicatorValueUnit,
} from './IndicatorValueEditor';

type Props={
  rows:KpiPlanRow[];
  candidates:IndicatorCandidate[];
  loading?:boolean;
  editable?:boolean;

  onRowsChange:(rows:KpiPlanRow[])=>void;
  onSave:(rows:KpiPlanRow[])=>Promise<void>;

  onAdd:(payload:{
    candidate:IndicatorCandidate;
    baselinevalue:number;
    targetvalue:number;
    thresholdmin?:number|null;
    thresholdmax?:number|null;
    description:string;
  })=>Promise<void>;
};

type State={
  addOpen:boolean;
  selectedCandidate:IndicatorCandidate|null;

  baselinevalue:number;
  targetvalue:number;
  thresholdmin:number;
  thresholdmax:number;
  description:string;
};

const recommendedNumber=(value:any)=>{
  if(
    value===null
    ||value===undefined
    ||value===''
    ||!Number.isFinite(Number(value))
  ){
    return null;
  }

  return Number(value);
};

export default class KpiPlanGridPanel
extends React.PureComponent<Props,State>{

  state:State={
    addOpen:false,
    selectedCandidate:null,

    baselinevalue:0,
    targetvalue:0,
    thresholdmin:0,
    thresholdmax:0,
    description:'',
  };

  private validate=(row:KpiPlanRow)=>{
    if(
      row.thresholdmin===null
      ||row.thresholdmin===undefined
      ||!Number.isFinite(Number(row.thresholdmin))
    ){
      return 'Warning Below is required.';
    }

    if(
      row.thresholdmax===null
      ||row.thresholdmax===undefined
      ||!Number.isFinite(Number(row.thresholdmax))
    ){
      return 'Warning Above is required.';
    }

    if(Number(row.thresholdmax)<Number(row.thresholdmin)){
      return 'Warning Above cannot be less than Warning Below.';
    }

    return '';
  };

  private unit=():IndicatorValueUnit=>{
    const candidate=this.state.selectedCandidate;

    return {
      code:candidate?.uomcode,
      name:candidate?.uomname,
      symbol:candidate?.uomsymbol,
      minimum:candidate?.uomminimum,
      maximum:candidate?.uommaximum,
      step:candidate?.uomstep,
      bounded:candidate?.uombounded,
    };
  };

  private selectCandidate=(
    selectedCandidate:IndicatorCandidate|null,
  )=>{
    const suggestedTarget=
      recommendedNumber(selectedCandidate?.suggestedtarget)
      ??0;

    const unitMinimum=
      recommendedNumber(selectedCandidate?.uomminimum);

    const unitMaximum=
      recommendedNumber(selectedCandidate?.uommaximum);

    const recommendedLow=
      recommendedNumber(selectedCandidate?.recommendedwarninglow);

    const recommendedHigh=
      recommendedNumber(selectedCandidate?.recommendedwarninghigh);

    this.setState({
      selectedCandidate,

      baselinevalue:
        recommendedNumber(selectedCandidate?.recommendedbaseline)
        ??0,

      targetvalue:suggestedTarget,

      thresholdmin:
        recommendedLow
        ??unitMinimum
        ??suggestedTarget,

      thresholdmax:
        recommendedHigh
        ??unitMaximum
        ??suggestedTarget,

      description:'',
    });
  };

  private processRowUpdate=(
    newRow:GridValidRowModel,
    oldRow:GridValidRowModel,
  ):KpiPlanRow=>{
    const updated:KpiPlanRow={
      ...(oldRow as KpiPlanRow),
      ...(newRow as KpiPlanRow),

      baselinevalue:Number(newRow.baselinevalue??0),
      targetvalue:Number(newRow.targetvalue??0),

      thresholdmin:
        newRow.thresholdmin===null
        ||newRow.thresholdmin===undefined
        ||newRow.thresholdmin===''
          ?null
          :Number(newRow.thresholdmin),

      thresholdmax:
        newRow.thresholdmax===null
        ||newRow.thresholdmax===undefined
        ||newRow.thresholdmax===''
          ?null
          :Number(newRow.thresholdmax),

      _state:'dirty',
      __dirty:true,
      __error:'',
    };

    updated.__error=this.validate(updated);

    this.props.onRowsChange(
      this.props.rows.map(
        row=>row.id===updated.id?updated:row,
      ),
    );

    return updated;
  };

  private canConfirm=()=>{
    const {
      selectedCandidate,
      baselinevalue,
      targetvalue,
      thresholdmin,
      thresholdmax,
    }=this.state;

    return (
      !!selectedCandidate
      &&Number.isFinite(Number(baselinevalue))
      &&Number.isFinite(Number(targetvalue))
      &&Number.isFinite(Number(thresholdmin))
      &&Number.isFinite(Number(thresholdmax))
      &&Number(thresholdmax)>=Number(thresholdmin)
    );
  };

  private add=async()=>{
    const candidate=this.state.selectedCandidate;

    if(!candidate) return;

    await this.props.onAdd({
      candidate,
      baselinevalue:Number(this.state.baselinevalue||0),
      targetvalue:Number(this.state.targetvalue||0),

      thresholdmin:Number(this.state.thresholdmin),
      thresholdmax:Number(this.state.thresholdmax),

      description:this.state.description,
    });

    this.setState({
      addOpen:false,
      selectedCandidate:null,
      baselinevalue:0,
      targetvalue:0,
      thresholdmin:0,
      thresholdmax:0,
      description:'',
    });
  };

  private save=async()=>{
    await this.props.onSave(this.props.rows);
  };

  private columns=():GridColDef[]=>[
    {
      field:'kpicode',
      headerName:'Indicator Code',
      width:180,
      editable:false,
      cellClassName:'indicator-cell-master',
    },
    {
      field:'kpiname',
      headerName:'Indicator',
      minWidth:280,
      flex:1,
      editable:false,
      cellClassName:'indicator-cell-master',
    },
    {
      field:'uomsymbol',
      headerName:'Unit',
      width:100,
      editable:false,
      cellClassName:'indicator-cell-master',
    },
    {
      field:'axiscode',
      headerName:'Axis',
      width:120,
      editable:false,
      cellClassName:'indicator-cell-master',
    },
    {
      field:'objectivecode',
      headerName:'Objective',
      width:150,
      editable:false,
      cellClassName:'indicator-cell-master',
    },
    {
      field:'baselinevalue',
      headerName:'Current Value',
      width:140,
      type:'number',
      editable:true,
      cellClassName:'indicator-cell-input',
    },
    {
      field:'targetvalue',
      headerName:'Target Value',
      width:140,
      type:'number',
      editable:true,
      cellClassName:'indicator-cell-input',
    },
    {
      field:'thresholdmin',
      headerName:'Warning Below',
      width:145,
      type:'number',
      editable:true,
      cellClassName:'indicator-cell-recommended',
    },
    {
      field:'thresholdmax',
      headerName:'Warning Above',
      width:145,
      type:'number',
      editable:true,
      cellClassName:'indicator-cell-recommended',
    },
    {
      field:'description',
      headerName:'Planning Notes',
      minWidth:240,
      flex:1,
      editable:true,
      cellClassName:'indicator-cell-input',
    },
    {
      field:'statecode',
      headerName:'State',
      width:110,
      editable:false,
      cellClassName:'indicator-cell-computed',
    },
    {
      field:'__error',
      headerName:'Validation',
      minWidth:250,
      editable:false,
      cellClassName:'indicator-cell-validation',
      renderCell:params=>
        params.value
          ?(
            <Typography
              variant="caption"
              color="error.main"
            >
              {params.value}
            </Typography>
          )
          :null,
    },
  ];

  private summaryCard=(
    icon:React.ReactNode,
    label:string,
    value?:string|null,
  )=>{
    if(!value) return null;

    return (
      <Paper variant="outlined" sx={{p:1.25,height:'100%'}}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box color="primary.main">
            {icon}
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="body2">
              {value}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    );
  };

  render(){
    const candidate=this.state.selectedCandidate;
    const unit=this.unit();

    const dirtyCount=this.props.rows.filter(
      row=>row.__dirty,
    ).length;

    const invalidCount=this.props.rows.filter(
      row=>Boolean(row.__error||this.validate(row)),
    ).length;

    const hasRecommendedTarget=
      recommendedNumber(candidate?.suggestedtarget)!==null;

    const hasRecommendedLow=
      recommendedNumber(candidate?.recommendedwarninglow)!==null;

    const hasRecommendedHigh=
      recommendedNumber(candidate?.recommendedwarninghigh)!==null;

    return (
      <>
        <FDataGrid
          title="Indicators"
          subtitle="Select reusable institutional Indicators and confirm their planning values for the current Planning Cycle and Cost Center."
          rows={this.props.rows}
          columns={this.columns()}
          loading={this.props.loading}
          height={390}
          getRowId={(row:KpiPlanRow)=>row.id}
          processRowUpdate={this.processRowUpdate}
          onProcessRowUpdateError={console.error}
          isCellEditable={params=>
            !!this.props.editable
            &&[
              'baselinevalue',
              'targetvalue',
              'thresholdmin',
              'thresholdmax',
              'description',
            ].includes(params.field)
          }
          toolbarActions={
            this.props.editable
              ?[
                  {
                    key:'add-indicator',
                    label:'Add Indicator',
                    icon:<AddIcon fontSize="small"/>,
                    onClick:()=>this.setState({addOpen:true}),
                  },
                ]
              :[]
          }
          saveAll={
            !this.props.editable
            ||dirtyCount===0
            ||invalidCount>0
              ?undefined
              :this.save
          }
          getRowClassName={params=>
            params.row.__error
              ?'indicator-row-error'
              :params.row.__dirty
                ?'indicator-row-dirty'
                :'indicator-row-clean'
          }
          sx={{
            '& .indicator-cell-input':{
              bgcolor:'warning.lighter',
              fontWeight:600,
            },
            '& .indicator-cell-recommended':{
              bgcolor:'success.lighter',
              fontWeight:600,
            },
            '& .indicator-cell-master':{
              bgcolor:'info.lighter',
              color:'text.secondary',
            },
            '& .indicator-cell-computed':{
              bgcolor:'action.hover',
              fontWeight:600,
            },
            '& .indicator-cell-validation':{
              color:'error.main',
            },
            '& .indicator-row-dirty':{
              boxShadow:'inset 3px 0 0 0',
              boxShadowColor:'warning.main',
            },
            '& .indicator-row-error':{
              bgcolor:'error.lighter',
            },
          }}
        />

        <Dialog
          open={this.state.addOpen}
          onClose={()=>this.setState({addOpen:false})}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Add Indicator</DialogTitle>

          <DialogContent>
            <Stack spacing={2} sx={{mt:1}}>
              {this.props.candidates.length===0&&(
                <Alert severity="info">
                  No additional reusable Indicators are available for this Cost Center and Planning Cycle.
                </Alert>
              )}

              <Autocomplete
                options={this.props.candidates}
                value={candidate}
                onChange={(_,value)=>this.selectCandidate(value)}
                getOptionLabel={option=>
                  `${option.shc} — ${option.nam}`
                }
                groupBy={option=>
                  `${option.axiscode??''} — ${option.axisname??''}`
                }
                isOptionEqualToValue={(a,b)=>a.rid===b.rid}
                renderInput={params=>(
                  <TextField
                    {...params}
                    label="Reusable Indicator"
                    helperText="Select an institutional Indicator to plan for this Cost Center."
                  />
                )}
              />

              {candidate&&(
                <>
                  <Alert severity="info">
                    <strong>{candidate.objectivecode}</strong>
                    {' — '}
                    {candidate.objectivename}
                  </Alert>

                  <Grid container spacing={1.5}>
                    <Grid size={{xs:12,sm:6}}>
                      {this.summaryCard(
                        <StraightenOutlinedIcon fontSize="small"/>,
                        'Measured in',
                        [
                          candidate.uomname,
                          candidate.uomsymbol
                            ?`(${candidate.uomsymbol})`
                            :'',
                        ].filter(Boolean).join(' '),
                      )}
                    </Grid>

                    <Grid size={{xs:12,sm:6}}>
                      {this.summaryCard(
                        <UpdateOutlinedIcon fontSize="small"/>,
                        'Measured',
                        candidate.frequencyname
                        ||candidate.frequencycode,
                      )}
                    </Grid>

                    <Grid size={{xs:12,sm:6}}>
                      {this.summaryCard(
                        <FunctionsOutlinedIcon fontSize="small"/>,
                        'Formula',
                        candidate.formula,
                      )}
                    </Grid>

                    <Grid size={{xs:12,sm:6}}>
                      {this.summaryCard(
                        <InsightsOutlinedIcon fontSize="small"/>,
                        'How to interpret it',
                        candidate.interpretation,
                      )}
                    </Grid>
                  </Grid>

                  {candidate.targetdescription&&(
                    <Alert severity="success">
                      <strong>Institutional recommendation:</strong>{' '}
                      {candidate.targetdescription}
                    </Alert>
                  )}

                  <Divider/>

                  <Grid container spacing={2}>
                    <Grid size={{xs:12,md:6}}>
                      <IndicatorValueEditor
                        label="Current Value"
                        helperText="Where are we today?"
                        value={this.state.baselinevalue}
                        onChange={baselinevalue=>
                          this.setState({
                            baselinevalue:Number(baselinevalue||0),
                          })
                        }
                        unit={unit}
                        recommended={
                          recommendedNumber(
                            candidate.recommendedbaseline,
                          )!==null
                        }
                      />
                    </Grid>

                    <Grid size={{xs:12,md:6}}>
                      <IndicatorValueEditor
                        label="Target Value"
                        helperText="Where do we want to be by the end of the planning cycle?"
                        value={this.state.targetvalue}
                        onChange={targetvalue=>
                          this.setState({
                            targetvalue:Number(targetvalue||0),
                          })
                        }
                        unit={unit}
                        recommended={hasRecommendedTarget}
                      />
                    </Grid>

                    <Grid size={{xs:12,md:6}}>
                      <IndicatorValueEditor
                        label="Warning Below"
                        helperText="Performance below this value will require attention."
                        value={this.state.thresholdmin}
                        onChange={thresholdmin=>
                          this.setState({
                            thresholdmin:Number(thresholdmin||0),
                          })
                        }
                        unit={unit}
                        recommended={hasRecommendedLow}
                      />
                    </Grid>

                    <Grid size={{xs:12,md:6}}>
                      <IndicatorValueEditor
                        label="Warning Above"
                        helperText="Performance above this value will require attention."
                        value={this.state.thresholdmax}
                        onChange={thresholdmax=>
                          this.setState({
                            thresholdmax:Number(thresholdmax||0),
                          })
                        }
                        unit={unit}
                        recommended={hasRecommendedHigh}
                      />
                    </Grid>
                  </Grid>

                  <Alert severity="info">
                    Confirm the recommended values or adjust them for this Planning Cycle.
                    Performance outside the warning limits will generate monitoring attention.
                  </Alert>

                  <TextField
                    multiline
                    minRows={3}
                    label="Planning Notes"
                    value={this.state.description}
                    onChange={event=>
                      this.setState({
                        description:event.target.value,
                      })
                    }
                    helperText="Explain any change from the institutional recommendation or record assumptions for this cycle."
                  />
                </>
              )}
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={()=>this.setState({addOpen:false})}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              disabled={!this.canConfirm()}
              onClick={this.add}
            >
              Confirm Indicator
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}
